import React from 'react';
import { Screen } from '../types';
import ArunachalEmblem from '../../../assets/Arunachal-Pradesh.png';

const ViewPermitScreen = ({ userData, purpose, startDate, endDate, navigateTo }) => {
  // Generate permit number from verification ID or random
  const generatePermitNo = () => {
    if (userData?.verificationId) {
      // Use verification ID to create permit number
      const id = userData.verificationId.replace(/-/g, '').slice(0, 16);
      return `ILP-${Date.now().toString().slice(-10)}${id.slice(0, 6)}`.toUpperCase();
    }
    return `ILP-${Date.now()}${Math.random().toString(36).slice(2, 8)}`.toUpperCase();
  };

  // Map purpose to category
  const getCategory = () => {
    if (purpose?.includes('Tourism') || purpose?.includes('Vacation')) return 'Tourist';
    if (purpose?.includes('Work') || purpose?.includes('Business')) return 'Business';
    if (purpose?.includes('Student') || purpose?.includes('Education')) return 'Student';
    return 'Tourist';
  };

  // Get photo URL - handle different formats from DigiLocker
  const getPhotoUrl = () => {
    // Check for base64 photo from DigiLocker
    if (userData?.photoUrl) {
      // If it's already a data URL, use it directly
      if (userData.photoUrl.startsWith('data:')) {
        return userData.photoUrl;
      }
      // If it's a base64 string without prefix, add it
      if (userData.photoUrl.length > 100) {
        return `data:image/jpeg;base64,${userData.photoUrl}`;
      }
      // Otherwise use as-is (might be a URL)
      return userData.photoUrl;
    }
    // Check for document URL from S3
    if (userData?.documentUrl) {
      return userData.documentUrl;
    }
    return null;
  };

  // Permit data using fetched Aadhaar data
  const permitData = {
    permitNo: generatePermitNo(),
    // Use Aadhaar verified data
    name: userData?.name || 'Not Available',
    dob: userData?.dob || 'Not Available',
    gender: userData?.gender || 'Not Available',
    mobile: userData?.mobile || 'Not Available',
    address: userData?.address || 'Not Available',
    photoUrl: getPhotoUrl(),
    aadhaar: userData?.aadhaar || 'XXXX-XXXX-XXXX',
    // Permit dates
    validFrom: startDate || new Date(),
    validTo: endDate || new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
    issueDate: new Date(),
    // Permit details
    category: getCategory(),
    applicationType: 'Single',
    district: 'Lower Subansiri',
    checkGates: 'Khemin, Holongi, Gumto, Naharlagun Railway Station',
    // Verification method
    verificationMethod: userData?.verificationId ? 'DigiLocker Verified' : 'Not Verified',
    verificationId: userData?.verificationId || null
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
  };

  const generateQRData = () => {
    // Include permit number and verification ID for QR verification
    const qrData = {
      permit: permitData.permitNo,
      name: permitData.name,
      validFrom: formatDate(permitData.validFrom),
      validTo: formatDate(permitData.validTo),
      verificationId: permitData.verificationId
    };
    return `https://eilp.arunachal.gov.in/verify?data=${encodeURIComponent(JSON.stringify(qrData))}`;
  };

  return (
    <div className="ilp-screen">
      <div className="ilp-header white-bg">
        <button className="ilp-back-btn" onClick={() => navigateTo(Screen.Success)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Official eILP</h2>
        <button className="ilp-icon-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
      </div>

      <div className="ilp-official-permit-scroll">
        <div className="ilp-official-permit">
          {/* Header with Emblem */}
          <div className="ilp-permit-gov-header">
            <div className="ilp-permit-emblem">
              <img src={ArunachalEmblem} alt="Arunachal Pradesh" className="ilp-emblem-image" />
            </div>
            <div className="ilp-permit-signature">
              <div className="ilp-signature-status">Signature Not Verified</div>
              <div className="ilp-signature-details">
                <p>Digitally signed by Administrator</p>
                <p>Date: {new Date().toISOString().split('T')[0]} IST</p>
                <p>Reason: Issue of Tourist eILP</p>
                <p>Location: eILP State Nodal Office</p>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="ilp-permit-title">
            <h1>Government of Arunachal Pradesh</h1>
            <p>Tourist eILP for Indian Nationals</p>
          </div>

          {/* Permit Details Section */}
          <div className="ilp-permit-section">
            <div className="ilp-section-header-bar">Permit Details</div>
            <div className="ilp-permit-details-grid">
              <div className="ilp-permit-qr-box">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(generateQRData())}`} 
                  alt="QR Code" 
                />
                <span>Scan to verify the genuinity of eILP</span>
              </div>
              <div className="ilp-permit-details-list">
                <div className="ilp-permit-detail-row">
                  <label>e-ILP valid from</label>
                  <span>{formatDate(permitData.validFrom)}</span>
                </div>
                <div className="ilp-permit-detail-row">
                  <label>e-ILP valid to</label>
                  <span>{formatDate(permitData.validTo)}</span>
                </div>
                <div className="ilp-permit-detail-row">
                  <label>permit Category</label>
                  <span>{permitData.category}</span>
                </div>
                <div className="ilp-permit-detail-row">
                  <label>Application Type</label>
                  <span>{permitData.applicationType}</span>
                </div>
                <div className="ilp-permit-detail-row">
                  <label>Date of Issue</label>
                  <span>{formatDate(permitData.issueDate)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* District and Check Gate */}
          <div className="ilp-permit-location-grid">
            <div className="ilp-permit-location-box">
              <div className="ilp-location-header">District</div>
              <div className="ilp-location-value">{permitData.district}</div>
            </div>
            <div className="ilp-permit-location-box wide">
              <div className="ilp-location-header">Check Gate</div>
              <div className="ilp-location-value">{permitData.checkGates}</div>
            </div>
          </div>

          {/* Primary Applicant Details */}
          <div className="ilp-permit-section">
            <div className="ilp-section-header-bar cyan">Primary Applicant Details</div>
            <div className="ilp-applicant-grid">
              <div className="ilp-applicant-photo-box">
                {permitData.photoUrl ? (
                  <img src={permitData.photoUrl} alt="Applicant" />
                ) : (
                  <div className="ilp-photo-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="ilp-applicant-details">
                <div className="ilp-permit-number">
                  <label>Permit No :</label>
                  <span>{permitData.permitNo}</span>
                </div>
                <div className="ilp-applicant-info-grid">
                  <div className="ilp-info-row">
                    <label>Name</label>
                    <span>{permitData.name}</span>
                  </div>
                  <div className="ilp-info-row">
                    <label>D.O.B.</label>
                    <span>{permitData.dob}</span>
                  </div>
                  <div className="ilp-info-row">
                    <label>Gender</label>
                    <span>{permitData.gender}</span>
                  </div>
                  <div className="ilp-info-row">
                    <label>Self Verified</label>
                    <span>{permitData.verificationMethod}</span>
                  </div>
                  <div className="ilp-info-row">
                    <label>Contact No</label>
                    <span>{permitData.mobile}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ilp-applicant-address">
              <label>Permanent Address:</label>
              <span>{permitData.address}</span>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="ilp-permit-terms">
            <h4>Terms & Conditions:</h4>
            <ol>
              <li>This is an online generated Permit and it is digitally signed.</li>
              <li>It is not a trading license, business permit, or identity card.</li>
              <li>The original document uploaded as ID proof must be produced at the check gate.</li>
              <li>The grantee shall not visit any place or travel or attempt to travel by any route other than those indicated in the eILP.</li>
              <li>This eILP is non-transferable and should be used only by the person whose name it is issued.</li>
              <li>This eILP is not renewable for extension of stay, the grantee shall obtain a fresh ILP as per the rules.</li>
              <li>The permit holder is not authorized to take photographs or film in restricted areas for photo and videography in the state.</li>
              <li>This pass shall not be used beyond the expiry period.</li>
              <li>If this eILP is tampered with or suspected of being altered, the eILP holder shall be booked under appropriate law.</li>
            </ol>
          </div>

          {/* Footer */}
          <div className="ilp-permit-footer">
            <span>https://www.eilp.arunachal.gov.in</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="ilp-permit-actions-bar">
        <button className="ilp-primary-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PDF
        </button>
        <button className="ilp-secondary-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </button>
      </div>
    </div>
  );
};

export default ViewPermitScreen;

